import React, { useEffect, useMemo, useState } from "react";
//-tech-debt-react-query;
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardHeader,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { fetchItems, fetchItem, postItem, putItem, deleteItem } from "../../support/queries";
import { HbrDivider } from "@harbor/elements-react-standalone";
import { CacheKey } from "../../types/cache-keys";
import AdminConfirmDialog from "../Admin/Common/AdminConfirmDialog";

type Permission = {
  id: string | number;
  name: string;
  model: string;
  description?: string | null;
  last_updated?: string;
};

export type Role = {
  id?: string | number;
  name: string;
  description?: string;
  permissions?: (string | number)[];
  __clone__?: boolean;
};

type Product = { id: number; name: string };
type GoToMarket = { id: number; name: string };

type ScopeType = "product" | "go_to_market";
type ScopedAction = "read" | "update" | "create" | "delete";

type RBACScope = {
  id: number;
  scope_type: "all" | ScopeType;
  object_id: number | null;
  label?: string | null;
};

type RolePermissionScopeRow = {
  id?: number | string;
  role: number | string;
  permission: number | string;
  scope: number | string;
};

interface Props {
  open: boolean;
  editItem: Role | null;
  onClose: () => void;
}

function not(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => !b.includes(value));
}
function intersection(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => b.includes(value));
}
function union(a: readonly string[], b: readonly string[]): string[] {
  return [...a, ...not(b, a)];
}

const isApiPerm = (name: string) => name.startsWith("api.");
const isPagePerm = (name: string) => name.startsWith("page.");

const ACTION_LABEL: Record<ScopedAction, string> = {
  read: "View",
  update: "Edit",
  create: "Create",
  delete: "Delete",
};

const PERMISSION_MODEL_BY_SCOPE: Record<ScopeType, string> = {
  product: "product",
  go_to_market: "go-to-market",
};

function displayPermissionName(p: Permission): string {
  if (isApiPerm(p.name)) return p.name.replace(/^api\./, "");
  if (isPagePerm(p.name)) return p.name.replace(/^page\./, "");
  return p.name;
}

function permissionGroupKey(p: Permission): string {
  if (isPagePerm(p.name)) return "ui-modules";
  if (isApiPerm(p.name)) return p.model?.trim() ? p.model : "api";
  return p.model?.trim() ? p.model : "other";
}

function groupPermissions(permissions: Permission[], filterIds: string[]) {
  const grouped: Record<string, Permission[]> = {};
  for (const perm of permissions) {
    if (!filterIds.includes(String(perm.id))) continue;
    const key = permissionGroupKey(perm);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(perm);
  }
  for (const k of Object.keys(grouped)) {
    grouped[k] = grouped[k]
      .slice()
      .sort((a, b) => displayPermissionName(a).localeCompare(displayPermissionName(b)));
  }
  return grouped;
}

async function getOrCreateRbacScope(params: {
  scope_type: ScopeType;
  object_id: number;
  label?: string;
}): Promise<RBACScope> {
  const existing = await fetchItems("db/rbac-scope", {
    scope_type: params.scope_type,
    object_id: params.object_id,
  });

  const list: RBACScope[] =
    Array.isArray(existing) ? existing : (existing?.items ?? existing?.records ?? []);

  if (list.length > 0) return list[0];

  return await postItem("db/rbac-scope", {
    scope_type: params.scope_type,
    object_id: params.object_id,
    label: params.label ?? null,
  });
}

const TRANSFER_LIST_WIDTH = 320;

const RoleEditDialog: React.FC<Props> = ({ open, editItem, onClose }) => {
  const role = editItem;
  const isClone = !!role?.__clone__;
  const queryClient = useQueryClient();

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [initialName, setInitialName] = useState("");
  const [initialDescription, setInitialDescription] = useState("");

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [assignedPermissions, setAssignedPermissions] = useState<string[]>([]);
  const [initialAssignedPermissions, setInitialAssignedPermissions] = useState<string[]>([]);
  const [checkedPermissions, setCheckedPermissions] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);

  const [leftPermissionSearch, setLeftPermissionSearch] = useState("");
  const [rightPermissionSearch, setRightPermissionSearch] = useState("");

  const [products, setProducts] = useState<Product[]>([]);
  const [gtms, setGtms] = useState<GoToMarket[]>([]);
  const [rbacScopes, setRbacScopes] = useState<RBACScope[]>([]);
  const [roleScopes, setRoleScopes] = useState<RolePermissionScopeRow[]>([]);
  const [initialRoleScopes, setInitialRoleScopes] = useState<RolePermissionScopeRow[]>([]);

  const [newScopeType, setNewScopeType] = useState<ScopeType>("product");
  const [newScopedAction, setNewScopedAction] = useState<ScopedAction>("read");
  const [newScopeObjectId, setNewScopeObjectId] = useState<number | "">("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const clonedName = role?.name ? `${role.name} - Copy` : "";

    setName(isClone ? clonedName : role?.name ?? "");
    setDescription(role?.description ?? "");
    setInitialName(isClone ? clonedName : role?.name ?? "");
    setInitialDescription(role?.description ?? "");
    setLeftPermissionSearch("");
    setRightPermissionSearch("");
    setExpanded(false);
    setLoading(true);

    const roleDetailPromise = role?.id
      ? fetchItem(`db/role/${role.id}`)
      : Promise.resolve({ permissions: [] });

    const roleScopesPromise = role?.id
      ? fetchItems("db/role-permission-scope", { role: role.id })
      : Promise.resolve([]);

    Promise.all([
      fetchItems("db/permission", {page: 1, size: 2000}),
      fetchItems("db/product", { vendor__is_cisco: true }),
      fetchItems("db/go-to-market"),
      fetchItems("db/rbac-scope"),
      roleDetailPromise,
      roleScopesPromise,
    ])
      .then(([allPerms, allProducts, allGtms, allScopes, roleDetail, scopes]) => {
        setPermissions(allPerms);
        setProducts(allProducts);
        setGtms(allGtms);

        const scopesList: RBACScope[] =
          Array.isArray(allScopes) ? allScopes : (allScopes?.items ?? allScopes?.records ?? []);
        setRbacScopes(scopesList);

        const assigned = ((roleDetail.permissions || []) as (string | number)[]).map(String);
        setAssignedPermissions(assigned);
        setInitialAssignedPermissions(assigned);
        setCheckedPermissions([]);

        const scopedRows = Array.isArray(scopes) ? scopes : (scopes?.items ?? scopes?.records ?? []);
        if (isClone) {
          setRoleScopes([]);
          setInitialRoleScopes([]);
        } else {
          setRoleScopes(scopedRows);
          setInitialRoleScopes(scopedRows);
        }

        setNewScopeType("product");
        setNewScopedAction("read");
        setNewScopeObjectId("");
      })
      .finally(() => setLoading(false));
  }, [open, role, isClone]);

  const hasUnsavedChanges = useMemo(() => {
    const a = [...assignedPermissions].sort();
    const b = [...initialAssignedPermissions].sort();

    const normalizeScopes = (rows: RolePermissionScopeRow[]) =>
      rows
        .map((r) => ({
          permission: String(r.permission),
          scope: String(r.scope),
        }))
        .sort((x, y) =>
          `${x.permission}-${x.scope}`.localeCompare(`${y.permission}-${y.scope}`)
        );

    return (
      name !== initialName ||
      description !== initialDescription ||
      JSON.stringify(a) !== JSON.stringify(b) ||
      JSON.stringify(normalizeScopes(roleScopes)) !== JSON.stringify(normalizeScopes(initialRoleScopes))
    );
  }, [
    name,
    description,
    initialName,
    initialDescription,
    assignedPermissions,
    initialAssignedPermissions,
    roleScopes,
    initialRoleScopes,
  ]);

  const handleRequestClose = () => {
    if (!hasUnsavedChanges || saving || loading) {
      onClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const allPermissionIds = useMemo(() => permissions.map((p) => String(p.id)), [permissions]);
  const leftPermissions = useMemo(
    () => not(allPermissionIds, assignedPermissions),
    [allPermissionIds, assignedPermissions]
  );
  const rightPermissions = assignedPermissions;

  const matchesPermissionSearch = (perm: Permission, term: string) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;
    const display = displayPermissionName(perm).toLowerCase();
    const name = (perm.name || "").toLowerCase();
    const model = (perm.model || "").toLowerCase();
    const description = (perm.description || "").toLowerCase();
    return (
      display.includes(q) ||
      name.includes(q) ||
      model.includes(q) ||
      description.includes(q)
    );
  };

  const filteredLeftPermissions = useMemo(
    () =>
      leftPermissions.filter((id) => {
        const perm = permissions.find((p) => String(p.id) === id);
        return perm ? matchesPermissionSearch(perm, leftPermissionSearch) : false;
      }),
    [leftPermissions, permissions, leftPermissionSearch]
  );

  const filteredRightPermissions = useMemo(
    () =>
      rightPermissions.filter((id) => {
        const perm = permissions.find((p) => String(p.id) === id);
        return perm ? matchesPermissionSearch(perm, rightPermissionSearch) : false;
      }),
    [rightPermissions, permissions, rightPermissionSearch]
  );

  const leftCheckedPermissions = intersection(checkedPermissions, leftPermissions);
  const rightCheckedPermissions = intersection(checkedPermissions, rightPermissions);

  const handleTogglePermission = (value: string) => () => {
    setCheckedPermissions((prev) => {
      const currentIndex = prev.indexOf(value);
      const newChecked = [...prev];
      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);
      return newChecked;
    });
  };

  const numberOfCheckedPermissions = (items: readonly string[]) =>
    intersection(checkedPermissions, items).length;

  const handleToggleAllPermissions = (items: readonly string[]) => () => {
    if (numberOfCheckedPermissions(items) === items.length) {
      setCheckedPermissions(not(checkedPermissions, items));
    } else {
      setCheckedPermissions(union(checkedPermissions, items));
    }
  };

  const handleCheckedRightPermissions = () => {
    setAssignedPermissions(union(assignedPermissions, leftCheckedPermissions));
    setCheckedPermissions(not(checkedPermissions, leftCheckedPermissions));
  };

  const handleCheckedLeftPermissions = () => {
    setAssignedPermissions(not(assignedPermissions, rightCheckedPermissions));
    setCheckedPermissions(not(checkedPermissions, rightCheckedPermissions));
  };

  const customAvailableList = (title: React.ReactNode, itemIds: readonly string[]) => {
    const grouped = groupPermissions(permissions, [...itemIds]);
    const groupEntries = Object.entries(grouped).sort(([a], [b]) => {
      if (a === "ui-modules") return -1;
      if (b === "ui-modules") return 1;
      return a.localeCompare(b);
    });

    return (
      <Card sx={{ minWidth: TRANSFER_LIST_WIDTH, width: TRANSFER_LIST_WIDTH }}>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          title={title}
          subheader={`${numberOfCheckedPermissions(itemIds)}/${itemIds.length} selected`}
        />
        <HbrDivider />
        <Box sx={{ height: 300, overflow: "auto", bgcolor: "background.paper" }}>
          {groupEntries.length === 0 && (
            <Typography align="center" sx={{ mt: 3, color: "text.secondary" }}>
              No available permissions
            </Typography>
          )}

          {groupEntries.map(([group, perms]) => {
            const permIds = perms.map((p) => String(p.id));
            const checkedCount = numberOfCheckedPermissions(permIds);

            return (
              <Accordion
                key={group}
                expanded={expanded === group}
                onChange={(_, isExp) => setExpanded(isExp ? group : false)}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Checkbox
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleAllPermissions(permIds)();
                    }}
                    checked={checkedCount === permIds.length && permIds.length !== 0}
                    indeterminate={checkedCount !== permIds.length && checkedCount !== 0}
                    disabled={permIds.length === 0}
                    size="small"
                  />
                  <Typography sx={{ ml: 1, fontWeight: 500 }}>
                    {group === "ui-modules" ? "UI Modules" : group}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <List dense disablePadding>
                    {perms.map((perm) => {
                      const id = String(perm.id);
                      const labelId = `transfer-list-item-${id}-label`;
                      return (
                        <ListItemButton
                          key={id}
                          role="listitem"
                          onClick={handleTogglePermission(id)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={checkedPermissions.includes(id)}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ "aria-labelledby": labelId }}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={labelId}
                            primary={displayPermissionName(perm)}
                            secondary={perm.description || undefined}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Card>
    );
  };

  const customAssignedList = (title: React.ReactNode, itemIds: readonly string[]) => (
    <Card sx={{ minWidth: TRANSFER_LIST_WIDTH, width: TRANSFER_LIST_WIDTH }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAllPermissions(itemIds)}
            checked={numberOfCheckedPermissions(itemIds) === itemIds.length && itemIds.length !== 0}
            indeterminate={
              numberOfCheckedPermissions(itemIds) !== itemIds.length && numberOfCheckedPermissions(itemIds) !== 0
            }
            disabled={itemIds.length === 0}
          />
        }
        title={title}
        subheader={`${numberOfCheckedPermissions(itemIds)}/${itemIds.length} selected`}
      />
      <HbrDivider />
      <List
        sx={{ width: TRANSFER_LIST_WIDTH, height: 300, bgcolor: "background.paper", overflow: "auto" }}
        dense
        component="div"
        role="list"
      >
        {itemIds.map((id) => {
          const perm = permissions.find((p) => String(p.id) === id);
          if (!perm) return null;
          const labelId = `transfer-list-item-${id}-label`;

          return (
            <ListItemButton key={id} role="listitem" onClick={handleTogglePermission(id)}>
              <ListItemIcon>
                <Checkbox
                  checked={checkedPermissions.includes(id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": labelId }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={displayPermissionName(perm)}
                secondary={perm.description || perm.model || undefined}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  const scopedPermissionId = useMemo(() => {
    const modelSlug = PERMISSION_MODEL_BY_SCOPE[newScopeType];
    const expectedName = `api.${modelSlug}.${newScopedAction}`;
    const p = permissions.find((x) => x.name === expectedName);
    return p ? String(p.id) : "";
  }, [permissions, newScopeType, newScopedAction]);

  const scopeObjectOptions = useMemo(() => {
    return newScopeType === "product" ? products : gtms;
  }, [newScopeType, products, gtms]);

  const scopeLabelForRow = (row: RolePermissionScopeRow) => {
    const sid = Number(row.scope);
    const s = rbacScopes.find((x) => x.id === sid);
    if (!s) return `scope:${sid}`;
    if (s.scope_type === "all") return "all";
    if (s.label && s.label.trim()) return s.label;
    return `${s.scope_type}:${s.object_id}`;
  };

  const permissionLabel = (permId: string | number) => {
    const p = permissions.find((x) => String(x.id) === String(permId));
    return p ? displayPermissionName(p) : String(permId);
  };

  const handleAddScopeRow = async () => {
    if (isClone || !role?.id) {
      alert("Save the cloned/new role first before adding scoped permissions.");
      return;
    }
    if (!scopedPermissionId) {
      alert("Missing permission in DB. Expected api.<model>.<read|update|create|delete>.");
      return;
    }
    if (newScopeObjectId === "") return;

    try {
      const label =
        newScopeType === "product"
          ? products.find((p) => p.id === newScopeObjectId)?.name
          : gtms.find((g) => g.id === newScopeObjectId)?.name;

      const scope = await getOrCreateRbacScope({
        scope_type: newScopeType,
        object_id: Number(newScopeObjectId),
        label: label ?? undefined,
      });

      setRbacScopes((prev) => (prev.some((x) => x.id === scope.id) ? prev : [...prev, scope]));

      const created = await postItem("db/role-permission-scope", {
        role: role.id,
        permission: Number(scopedPermissionId),
        scope: scope.id,
      });

      setRoleScopes((prev) => [...prev, created]);
      setNewScopeObjectId("");
    } catch (e: any) {
      alert(e?.message || "Failed to add scoped permission.");
    }
  };

  const handleDeleteScopeRow = async (row: RolePermissionScopeRow) => {
    if (!row.id) return;
    await deleteItem(`db/role-permission-scope/${row.id}`);
    setRoleScopes((prev) => prev.filter((x) => x.id !== row.id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let roleId = isClone ? undefined : role?.id;
      let responseRole: any = null;

      if (!roleId) {
        responseRole = await postItem("db/role", {
          name,
          description,
          permissions: assignedPermissions,
        });
        roleId = responseRole.id;
      } else {
        responseRole = await putItem("db/role", {
          id: roleId,
          name,
          description,
          permissions: assignedPermissions,
        });
      }

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, "roles"] });
      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      alert("Failed to save role or permissions.");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isClone ? "Clone Role" : role?.id ? "Edit Role" : "Add Role"}
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="normal"
          />

          <Grid container spacing={2} sx={{ justifyContent: "center", alignItems: "center", marginTop: 2 }}>
            <Grid item>
              <Box sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search available permissions"
                  value={leftPermissionSearch}
                  onChange={(e) => setLeftPermissionSearch(e.target.value)}
                />
              </Box>
              {customAvailableList("Available Permissions", filteredLeftPermissions)}
            </Grid>

            <Grid item>
              <Grid container direction="column" sx={{ alignItems: "center" }}>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRightPermissions}
                  disabled={leftCheckedPermissions.length === 0}
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeftPermissions}
                  disabled={rightCheckedPermissions.length === 0}
                >
                  &lt;
                </Button>
              </Grid>
            </Grid>

            <Grid item>
              <Box sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search assigned permissions"
                  value={rightPermissionSearch}
                  onChange={(e) => setRightPermissionSearch(e.target.value)}
                />
              </Box>
              {customAssignedList("Assigned Permissions", filteredRightPermissions)}
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Scoped Permissions (Product / Go-To-Market)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assign View/Edit/Create/Delete access to a specific Product or Go-To-Market ID.
            </Typography>

            <Card variant="outlined">
              <CardHeader title="Add Scoped Permission" sx={{ py: 1.5 }} />
              <Divider />
              <Box
                sx={{
                  p: 2,
                  display: "grid",
                  gridTemplateColumns: "200px 200px 1fr 40px",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Access</InputLabel>
                  <Select
                    label="Access"
                    value={newScopedAction}
                    onChange={(e) => setNewScopedAction(e.target.value as ScopedAction)}
                  >
                    {(["read", "update", "create", "delete"] as ScopedAction[]).map((a) => (
                      <MenuItem key={a} value={a}>
                        {ACTION_LABEL[a]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Scope Type</InputLabel>
                  <Select
                    label="Scope Type"
                    value={newScopeType}
                    onChange={(e) => {
                      const t = e.target.value as ScopeType;
                      setNewScopeType(t);
                      setNewScopedAction("read");
                      setNewScopeObjectId("");
                    }}
                  >
                    <MenuItem value="product">product</MenuItem>
                    <MenuItem value="go_to_market">go-to-market</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>{newScopeType === "product" ? "Product" : "Go-To-Market"}</InputLabel>
                  <Select
                    label={newScopeType === "product" ? "Product" : "Go-To-Market"}
                    value={newScopeObjectId === "" ? "" : String(newScopeObjectId)}
                    onChange={(e) => setNewScopeObjectId(Number(e.target.value))}
                  >
                    {scopeObjectOptions.map((x: any) => (
                      <MenuItem key={String(x.id)} value={String(x.id)}>
                        {x.name ?? x.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton
                  onClick={handleAddScopeRow}
                  disabled={!scopedPermissionId || newScopeObjectId === "" || saving || loading}
                  aria-label="add scoped permission"
                >
                  <AddIcon />
                </IconButton>
              </Box>

              {!scopedPermissionId && (
                <Typography sx={{ px: 2, pb: 2 }} color="error" variant="body2">
                  Missing required permission in DB for this selection (expected{" "}
                  <code>{`api.${PERMISSION_MODEL_BY_SCOPE[newScopeType]}.${newScopedAction}`}</code>).
                </Typography>
              )}
            </Card>

            <Box sx={{ mt: 2 }}>
              <Card variant="outlined">
                <CardHeader title="Current Scoped Assignments" sx={{ py: 1.5 }} />
                <Divider />
                <List dense>
                  {roleScopes.length === 0 && (
                    <Typography sx={{ p: 2 }} color="text.secondary">
                      No scoped permissions assigned.
                    </Typography>
                  )}

                  {roleScopes.map((row) => (
                    <ListItemButton
                      key={String(row.id ?? `${row.permission}-${row.scope}`)}
                      disabled
                    >
                      <ListItemText
                        primary={`${permissionLabel(row.permission)}  →  ${scopeLabelForRow(row)}`}
                      />
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteScopeRow(row)}
                        aria-label="delete scoped permission"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemButton>
                  ))}
                </List>
              </Card>
            </Box>
          </Box>

          {(loading || saving) && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleRequestClose} disabled={loading || saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message="You have unsaved changes in this role editor. If you continue, your changes will be lost."
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  );
};

export default RoleEditDialog;